
import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { BookingType } from "@/lib/supabase/types";
import { Calendar, Clock, User, Phone, Mail, AlertTriangle, DollarSign, Users, Archive } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface BookingHistoryProps {
  bookings: BookingType[];
  isLoading: boolean;
  isBookingPast: (booking: BookingType) => boolean;
}

export const BookingHistory: React.FC<BookingHistoryProps> = ({
  bookings,
  isLoading,
  isBookingPast,
}) => {
  const { t } = useTranslation();
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-36 bg-card animate-pulse rounded-xl border border-border"></div>
        ))}
      </div>
    );
  }
  
  if (bookings.length === 0) {
    return (
      <div className="text-center py-12 bg-card border border-border rounded-xl">
        <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
          <Archive className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-medium mb-2">{t("dashboard.bookings.none")}</h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">
          {t("dashboard.bookings.noneDesc")}
        </p>
      </div>
    );
  }
  
  return (
    <div className="grid gap-6">
      {bookings.map((booking, index) => {
        const bookingDate = booking.createdAt 
          ? new Date(booking.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) 
          : t("common.unknownDate");
        
        const classDate = booking.classDate && booking.classDate.length > 0
          ? new Date(booking.classDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : booking.classDay || t("common.unknownDate");
        
        return (
          <motion.div
            key={booking.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="bg-card border border-border rounded-xl overflow-hidden"
          >
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-display font-medium mb-1">{booking.className}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm text-muted-foreground">
                      {t("dashboard.bookings.bookedOn")} {bookingDate}
                      {booking.bookingType === 'subscription' && (
                        <span className="ml-2 text-primary">• {t("dashboard.bookings.usingSubscription")}</span>
                      )}
                    </p>
                    {booking.groupSize && booking.groupSize > 1 && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                        <Users className="h-3 w-3 mr-1" /> 
                        {t("dashboard.bookings.groupBooking")} ({booking.groupSize})
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex items-center gap-3">
                  {booking.paymentStatus === 'paid' ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {t("dashboard.bookings.paid")}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {t("dashboard.bookings.paymentPending")}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3">{t("dashboard.bookings.classDetails")}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{classDate}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{booking.classTime}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium mb-3">{t("dashboard.bookings.yourInformation")}</h4>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{booking.firstName} {booking.lastName}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Mail className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{booking.email}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Phone className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>{booking.phone}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {booking.groupSize && booking.groupSize > 1 && booking.groupNames && booking.groupNames.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                    {t("dashboard.bookings.groupAttendees")}
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {booking.groupNames.map((name, i) => (
                      <li key={i} className="text-sm px-3 py-1.5 bg-muted/50 rounded-md">
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};
